// src/pages/MainPanel/CreateExperiment/components/ImageSelector.jsx
//
// Lets users pick a container image from two EFabric GitLab registry
// subgroups, or type one manually. A single TextField shows the selected
// value. Also works as a live search filter for the list below.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Link,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { gitlabApiPrefix } from "../../../../components/SupabaseClient";
import { fetchAllGitLabPages } from "../../../../utils/gitlabPagination";
import RegistryTagTable from "../../components/RegistryTagTable";

const SUBGROUPS = [
  { label: "Base Images", subgroup: "efabric/base-images" },
  { label: "Experiments", subgroup: "efabric/experiments" },
];

export const REGISTRY_DISPLAY_PREFIX = "registry.efabric.cs.wisc.edu/efabric/";
function stripDisplayPrefix(loc) {
  return loc?.startsWith(REGISTRY_DISPLAY_PREFIX)
    ? loc.slice(REGISTRY_DISPLAY_PREFIX.length)
    : (loc ?? "");
}

// hostname/path/image:tag — hostname must contain at least one dot
const IMAGE_REF_REGEX =
  /^[a-z0-9][a-z0-9._-]*(?:\.[a-z0-9._-]+)+(\/[a-z0-9._-]+)+:[a-z0-9._-]+$/i;

// Short image path without hostname: path/image:tag
const SHORT_REF_REGEX = /^[a-z0-9][a-z0-9._-]*(\/[a-z0-9._-]+)+:[a-z0-9._-]+$/i;

/**
 * Returns an error string when the value is structurally invalid, or "".
 * Existence/registry policy is checked against the loaded EFabric catalog.
 */
export function validateImageRef(value) {
  if (!value?.trim()) return "Image reference is required.";
  const v = value.trim();
  if (SHORT_REF_REGEX.test(v) || IMAGE_REF_REGEX.test(v)) return "";
  return "Must be a valid image reference: path/image:tag";
}

/**
 * Expands a short EFabric image path to a full registry reference.
 * Full EFabric references are returned unchanged.
 */
export function expandImageRef(ref) {
  const value = ref?.trim();
  if (!value) return value;
  return value.startsWith(REGISTRY_DISPLAY_PREFIX)
    ? value
    : `${REGISTRY_DISPLAY_PREFIX}${value}`;
}

/**
 * A single registry repository row with a collapsible tag-detail table.
 * Tag details (size, digest, published) are fetched lazily on first expand.
 *
 * repo shape: { id, location, name, projectId, projectName, tags: string[] }
 */
function RepoRow({ repo, imageRef, onImageRefChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [tagDetails, setTagDetails] = useState(null); // null = not yet fetched
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const fetchDoneRef = useRef(false);

  const fetchTagDetails = useCallback(async () => {
    if (tagDetails !== null || fetchDoneRef.current) return;
    fetchDoneRef.current = true;
    setDetailsLoading(true);
    setDetailsError(null);
    try {
      const results = await Promise.all(
        repo.tags.map(async (tagName) => {
          const resp = await fetch(
            `${gitlabApiPrefix}/api/v4/projects/${repo.projectId}/registry/repositories/${repo.id}/tags/${encodeURIComponent(tagName)}`,
          );
          if (!resp.ok) return { name: tagName };
          return resp.json();
        }),
      );
      setTagDetails(
        results.map((t) => ({
          name: t.name,
          size: t.total_size ?? null,
          digest: t.digest ?? null,
          createdAt: t.created_at ?? null,
        })),
      );
    } catch (err) {
      fetchDoneRef.current = false;
      setDetailsError(err.message);
    } finally {
      setDetailsLoading(false);
    }
  }, [repo, tagDetails]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchTagDetails();
  };

  return (
    <Box sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
      {/* Header row */}
      <Box
        onClick={handleToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 1,
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.78rem",
            wordBreak: "break-all",
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          {stripDisplayPrefix(repo.location)}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexShrink: 0,
            ml: 1,
          }}
        >
          <Chip
            label={`${repo.tags.length} tag${repo.tags.length !== 1 ? "s" : ""}`}
            size="small"
            sx={{ height: 20, fontSize: "0.7rem" }}
          />
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              transition: "transform 0.2s",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </Box>
      </Box>

      {/* Expanded tag detail table */}
      <Collapse in={open} unmountOnExit>
        {detailsLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
            <CircularProgress size={18} />
          </Box>
        )}
        {detailsError && (
          <Alert severity="warning" sx={{ mx: 1.5, my: 1 }}>
            {detailsError}
          </Alert>
        )}
        {tagDetails && (
          <Box sx={{ maxHeight: 220, overflow: "auto" }}>
            <RegistryTagTable
              location={repo.location}
              tags={tagDetails}
              selectedRef={imageRef}
              onTagSelect={onImageRefChange}
              disabled={disabled}
            />
          </Box>
        )}
      </Collapse>
    </Box>
  );
}

RepoRow.propTypes = {
  repo: PropTypes.shape({
    id: PropTypes.number.isRequired,
    location: PropTypes.string.isRequired,
    projectId: PropTypes.number.isRequired,
    projectName: PropTypes.string.isRequired,
    tags: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  imageRef: PropTypes.string.isRequired,
  onImageRefChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default function ImageSelector({
  imageRef,
  onImageRefChange,
  onImageRefErrorChange,
  disabled,
}) {
  const [subgroupTab, setSubgroupTab] = useState(0);
  const currentSubgroup = SUBGROUPS[subgroupTab].subgroup;

  // reposCache: { [subgroup]: { loading: bool, error: string|null, data: repo[]|null } }
  const [reposCache, setReposCache] = useState({});

  // Fetch repos for the active subgroup (lazy, cached)
  useEffect(() => {
    const sub = currentSubgroup;

    // Guard: skip if already loading or loaded (requires non-null array).
    const existing = reposCache[sub];
    if (existing?.loading || Array.isArray(existing?.data)) return;

    const controller = new AbortController();
    const { signal } = controller;

    setReposCache((prev) => ({
      ...prev,
      [sub]: { loading: true, error: null, data: null },
    }));

    (async () => {
      try {
        const encoded = encodeURIComponent(sub);
        const projects = await fetchAllGitLabPages(
          `${gitlabApiPrefix}/api/v4/groups/${encoded}/projects?per_page=100&order_by=name&sort=asc`,
          { signal },
        );

        const allRepos = [];
        await Promise.all(
          projects.map(async (project) => {
            try {
              const repos = await fetchAllGitLabPages(
                `${gitlabApiPrefix}/api/v4/projects/${project.id}/registry/repositories?per_page=100`,
                { signal },
              );

              await Promise.all(
                repos.map(async (repo) => {
                  try {
                    const tags = await fetchAllGitLabPages(
                      `${gitlabApiPrefix}/api/v4/projects/${project.id}/registry/repositories/${repo.id}/tags?per_page=100`,
                      { signal },
                    );
                    if (tags.length > 0) {
                      allRepos.push({
                        id: repo.id,
                        location: repo.location,
                        name: repo.name,
                        projectId: project.id,
                        projectName: project.name,
                        projectDescription: project.description || null,
                        tags: tags.map((t) => t.name),
                      });
                    }
                  } catch (e) {
                    if (e.name !== "AbortError") {
                      /* skip inaccessible repo */
                    }
                  }
                }),
              );
            } catch (e) {
              if (e.name !== "AbortError") {
                /* skip inaccessible project */
              }
            }
          }),
        );

        allRepos.sort((a, b) => a.location.localeCompare(b.location));

        setReposCache((prev) => ({
          ...prev,
          [sub]: { loading: false, error: null, data: allRepos },
        }));
      } catch (err) {
        if (err.name === "AbortError") {
          // Aborted: reset so the next effect run starts a new fetch.
          setReposCache((prev) => {
            const entry = prev[sub];
            if (entry?.loading && entry?.data === null) {
              const { [sub]: _removed, ...rest } = prev;
              return rest;
            }
            return prev;
          });
          return;
        }
        setReposCache((prev) => ({
          ...prev,
          [sub]: { loading: false, error: err.message, data: null },
        }));
      }
    })();

    return () => controller.abort();
  }, [currentSubgroup]); // eslint-disable-line react-hooks/exhaustive-deps

  // Collect all known short refs across every loaded subgroup
  const allKnownRefs = useMemo(() => {
    const refs = new Set();
    Object.values(reposCache).forEach(({ data }) => {
      (data ?? []).forEach((repo) => {
        repo.tags.forEach((t) =>
          refs.add(stripDisplayPrefix(`${repo.location}:${t}`)),
        );
      });
    });
    return refs;
  }, [reposCache]);

  // Error shown in the text field
  const inputError = useMemo(() => {
    if (!imageRef) return "";
    const formatError = validateImageRef(imageRef);
    if (formatError) return formatError;

    const anyLoaded = Object.values(reposCache).some((c) => c.data !== null);
    if (!anyLoaded) return "";
    if (!allKnownRefs.has(stripDisplayPrefix(imageRef.trim()))) {
      return "Image not found";
    }
    return "";
  }, [imageRef, reposCache, allKnownRefs]);

  useEffect(() => {
    onImageRefErrorChange?.(inputError);
  }, [inputError, onImageRefErrorChange]);

  const currentCache = reposCache[currentSubgroup];

  // Filter current subgroup repos by the input value (acts as search)
  const filteredRepos = useMemo(() => {
    const repos = currentCache?.data ?? [];
    if (!imageRef.trim()) return repos;
    const q = imageRef.toLowerCase();
    return repos.filter((repo) => {
      const shortLoc = stripDisplayPrefix(repo.location).toLowerCase();
      return (
        shortLoc.includes(q) ||
        repo.tags.some((t) => `${shortLoc}:${t}`.toLowerCase().includes(q))
      );
    });
  }, [currentCache, imageRef]);

  // Group filtered repos by project (preserving alphabetical order)
  const groupedRepos = useMemo(() => {
    const groups = new Map(); // Map<projectName, { description, repos }>
    filteredRepos.forEach((repo) => {
      if (!groups.has(repo.projectName))
        groups.set(repo.projectName, {
          description: repo.projectDescription,
          repos: [],
        });
      groups.get(repo.projectName).repos.push(repo);
    });
    return [...groups.entries()]; // [ [projectName, { description, repos }], … ]
  }, [filteredRepos]);

  return (
    <Box>
      {/* Unified input (value + live search filter) */}
      <TextField
        fullWidth
        value={imageRef}
        onChange={(e) => onImageRefChange(stripDisplayPrefix(e.target.value))}
        disabled={disabled}
        error={!!inputError}
        helperText={
          inputError || (
            <>
              Select an image below, or type an image reference.{" "}
              <Link
                href="/workspace?tab=overview%2Fworkflows"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: "inherit" }}
              >
                Workflow Templates
              </Link>
            </>
          )
        }
        placeholder={"base-images/cpu-base:latest"}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: imageRef ? (
              <InputAdornment position="end">
                <Tooltip title="Clear">
                  <IconButton
                    size="small"
                    sx={{ p: 0.25 }}
                    onClick={() => onImageRefChange("")}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : null,
          },
        }}
        sx={{ mb: 2 }}
      />

      {/* Subgroup tabs */}
      <Tabs
        value={subgroupTab}
        onChange={(_e, v) => setSubgroupTab(v)}
        variant="fullWidth"
        sx={{ borderBottom: "1px solid", borderColor: "divider" }}
      >
        {SUBGROUPS.map((sg, i) => (
          <Tab
            key={sg.subgroup}
            label={sg.label}
            value={i}
            sx={{ textTransform: "none", fontSize: "0.875rem" }}
          />
        ))}
      </Tabs>

      {/* Repo list */}
      <Box
        sx={{
          border: "1px solid",
          borderTop: 0,
          borderColor: "divider",
          borderRadius: "0 0 4px 4px",
          maxHeight: 420,
          overflow: "auto",
        }}
      >
        {/* Loading */}
        {(currentCache?.loading || !currentCache) && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {/* Fetch error */}
        {currentCache?.error && (
          <Alert severity="warning" sx={{ m: 1 }}>
            Could not load registry: {currentCache.error}
          </Alert>
        )}

        {/* Empty search result */}
        {currentCache?.data && groupedRepos.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No images match your search.
          </Typography>
        )}

        {/* Grouped repo rows */}
        {currentCache?.data &&
          groupedRepos.map(([projectName, { description, repos }]) => (
            <Box key={projectName}>
              {/* Project header (sticky) */}
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  bgcolor: "grey.100",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  display: "flex",
                  alignItems: "baseline",
                  gap: 1.5,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    flexShrink: 0,
                  }}
                >
                  {projectName}
                </Typography>
                {description && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      opacity: 0.75,
                    }}
                  >
                    {description}
                  </Typography>
                )}
              </Box>

              {repos.map((repo) => (
                <RepoRow
                  key={repo.id}
                  repo={repo}
                  imageRef={imageRef}
                  onImageRefChange={onImageRefChange}
                  disabled={disabled}
                />
              ))}
            </Box>
          ))}
      </Box>
    </Box>
  );
}

ImageSelector.propTypes = {
  imageRef: PropTypes.string.isRequired,
  onImageRefChange: PropTypes.func.isRequired,
  onImageRefErrorChange: PropTypes.func,
  disabled: PropTypes.bool,
};
